/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { DiagnosticLogger } from "./DiagnosticLogger";
import { OperationalLogger } from "./OperationalLogger";
import { DiagnosticSeverity, type LogDetails } from "../types";
import { AxiosErrorAdapter } from "../errorMapper/adapters/AxiosErrorAdapter";
import { ErrorCodeUtil } from "../utility/ErrorCodeUtil";
import { PlaybackSetupErrorCode } from "../videoplayer/error/PlaybackErrorCodes";

const VIDEO_PROVIDER_CONFIGURATION_DIAGNOSTIC_EVENT: string =
  "video_provider_configuration";

/** `video_provider_configuration` diagnostic discriminators. */
const VideoProviderConfigurationEvent = {
  FetchSucceeded: "fetch_succeeded",
  EmptyResponse: "empty_response",
  InvalidEntries: "invalid_entries",
  FetchFailed: "fetch_failed",
  CacheUnavailable: "cache_unavailable",
  ProviderNotFound: "provider_not_found",
  DrmConfigInvalid: "drm_config_invalid",
  PlaybackFailed: "playback_failed"
} as const;

type VideoProviderConfigurationEventName =
  (typeof VideoProviderConfigurationEvent)[keyof typeof VideoProviderConfigurationEvent];

/** What caused a video provider configurations fetch. */
export const VideoProviderConfigurationTrigger = {
  Startup: "startup",
  Playback: "playback"
} as const;

export type VideoProviderConfigurationTriggerName =
  (typeof VideoProviderConfigurationTrigger)[keyof typeof VideoProviderConfigurationTrigger];

/** Caller-supplied video provider configuration telemetry fields. */
export type VideoProviderConfigurationLogFields = Record<
  string,
  string | undefined
>;

/** The minimum a configuration entry must carry to be counted as usable. */
interface VideoProviderConfigurationEntry {
  providerPartnerId?: string | null;
}

const KEY_EVENT: string = "event";
const KEY_ERROR_CODE: string = "error_code";
const KEY_SUB_ERROR_CODE: string = "sub_error_code";
const KEY_ERROR_TYPE: string = "error_type";
const KEY_TRIGGER: string = "trigger";
const KEY_PROVIDER_COUNT: string = "provider_count";
const KEY_INVALID_COUNT: string = "invalid_count";
const KEY_PROVIDER_PARTNER_ID: string = "provider_partner_id";
const KEY_DRM_SERVER_TYPE: string = "drm_server_type";
const KEY_MISSING_FIELDS: string = "missing_fields";
const KEY_DRM_ERROR_COUNT: string = "drm_error_count";

const MESSAGE_EMPTY_RESPONSE: string =
  "Video provider configurations response carried no configurations";
const MESSAGE_FETCH_FAILED: string =
  "Video provider configurations fetch failed";
const MESSAGE_PROVIDER_NOT_FOUND: string =
  "Video provider configurations hold no entry for the provider";
const MESSAGE_DRM_CONFIG_INVALID: string =
  "Video provider DRM configuration is incomplete";
const MESSAGE_PLAYBACK_FAILED: string =
  "Playback failed on video provider configuration";

/**
 * Error codes for the Video Provider Configurations failure scenarios. Taken
 * from the shared playback catalog so a code logged here matches the code the
 * player surfaces when DRM setup gives up (`VideoPlaybackController.onDrmFail`).
 *
 * - Configuration unavailable → `V934`: the request failed, the response
 *   carried no configurations, or it held no entry for the provider being
 *   tuned. A failed request carries the {@link ErrorCodeUtil} sub-error code
 *   of the layer that failed (`V934-N603`, `V934-G500`, …).
 * - Configuration present but unusable → `V909`: entries without a
 *   `providerPartnerId`, or a DRM configuration missing required fields.
 */
export const VideoProviderConfigurationErrorCode = {
  UNAVAILABLE: PlaybackSetupErrorCode.VP_CONFIGURATIONS_EMPTY_RESPONSE,
  MISCONFIGURATION: PlaybackSetupErrorCode.VP_CONFIGURATIONS_MISCONFIGURATION
} as const;

/**
 * Structured telemetry facade for the Video Provider Configurations flow: the
 * startup fetch, the on-demand refetch on tune, per-provider lookups, DRM
 * configuration validation, and the playback failure DRM setup ends in.
 *
 * The request itself is reported by `PostSigninLogger` (`apiRequest`,
 * `api_name=videoProviderConfiguration`); this logger reports what the flow
 * did with the result. All details are pre-redacted: never raw error
 * messages, tokens, or DRM URLs/headers.
 */
export class VideoProviderConfigurationLogger {
  private static readonly adapter: AxiosErrorAdapter = new AxiosErrorAdapter();

  /**
   * `error_code` / `sub_error_code` for a failed configurations request.
   * The sub-error code is omitted when no layer recognised the failure.
   */
  public static getFailureFields(
    error: unknown
  ): VideoProviderConfigurationLogFields {
    const subErrorCode: string = ErrorCodeUtil.extractSubErrorCode(
      error,
      VideoProviderConfigurationLogger.adapter
    );
    return {
      [KEY_ERROR_CODE]: VideoProviderConfigurationErrorCode.UNAVAILABLE,
      [KEY_SUB_ERROR_CODE]: subErrorCode.length > 0 ? subErrorCode : undefined
    };
  }

  /**
   * Reports the configurations a fetch resolved. No configurations at all is
   * an `empty_response` failure (`V934`); entries without a
   * `providerPartnerId` cannot be looked up and are an `invalid_entries`
   * warning (`V909`).
   */
  public static logFetchResult(
    trigger: VideoProviderConfigurationTriggerName,
    configs:
      | ReadonlyArray<VideoProviderConfigurationEntry | null | undefined>
      | Record<string, VideoProviderConfigurationEntry | null | undefined>
      | null
      | undefined
  ): void {
    const entries: Array<VideoProviderConfigurationEntry | null | undefined> =
      configs === null || configs === undefined ? [] : Object.values(configs);

    if (entries.length === 0) {
      VideoProviderConfigurationLogger.log(
        VideoProviderConfigurationEvent.EmptyResponse,
        MESSAGE_EMPTY_RESPONSE,
        DiagnosticSeverity.Warning,
        {
          [KEY_ERROR_CODE]: VideoProviderConfigurationErrorCode.UNAVAILABLE,
          [KEY_TRIGGER]: trigger
        }
      );
      OperationalLogger.logError({
        errorCode: VideoProviderConfigurationErrorCode.UNAVAILABLE,
        errorMessage: MESSAGE_EMPTY_RESPONSE
      });
      return;
    }

    const invalidCount: number = entries.filter(
      (entry: VideoProviderConfigurationEntry | null | undefined): boolean =>
        entry === null ||
        entry === undefined ||
        entry.providerPartnerId === null ||
        entry.providerPartnerId === undefined ||
        entry.providerPartnerId === ""
    ).length;
    const details: VideoProviderConfigurationLogFields = {
      [KEY_TRIGGER]: trigger,
      [KEY_PROVIDER_COUNT]: String(entries.length)
    };

    if (invalidCount > 0) {
      VideoProviderConfigurationLogger.log(
        VideoProviderConfigurationEvent.InvalidEntries,
        "Video provider configurations response has entries without a providerPartnerId",
        DiagnosticSeverity.Warning,
        {
          ...details,
          [KEY_INVALID_COUNT]: String(invalidCount),
          [KEY_ERROR_CODE]: VideoProviderConfigurationErrorCode.MISCONFIGURATION
        }
      );
      return;
    }

    VideoProviderConfigurationLogger.log(
      VideoProviderConfigurationEvent.FetchSucceeded,
      "Video provider configurations resolved",
      DiagnosticSeverity.Info,
      details
    );
  }

  /**
   * A fetch failed and was handled silently: startup carries on, and a tune
   * relying on the configuration fails DRM setup with `V934`.
   */
  public static logFetchFailed(
    trigger: VideoProviderConfigurationTriggerName,
    error: unknown
  ): void {
    const failureFields: VideoProviderConfigurationLogFields =
      VideoProviderConfigurationLogger.getFailureFields(error);
    VideoProviderConfigurationLogger.log(
      VideoProviderConfigurationEvent.FetchFailed,
      MESSAGE_FETCH_FAILED,
      DiagnosticSeverity.Warning,
      {
        ...failureFields,
        [KEY_ERROR_TYPE]: OperationalLogger.describeErrorType(error),
        [KEY_TRIGGER]: trigger
      }
    );
    OperationalLogger.logError({
      errorCode: VideoProviderConfigurationLogger.toDisplayCode(failureFields),
      errorMessage: MESSAGE_FETCH_FAILED
    });
  }

  /**
   * The cached configurations could not be read for a tune; the flow
   * recovers by refetching, so no error code is attached.
   */
  public static logCacheUnavailable(
    providerPartnerId: string,
    error: unknown
  ): void {
    VideoProviderConfigurationLogger.log(
      VideoProviderConfigurationEvent.CacheUnavailable,
      "Cached video provider configurations unavailable; refetching",
      DiagnosticSeverity.Info,
      {
        [KEY_PROVIDER_PARTNER_ID]: providerPartnerId,
        [KEY_ERROR_TYPE]: OperationalLogger.describeErrorType(error)
      }
    );
  }

  /** A fresh fetch succeeded but held no entry for the provider (`V934`). */
  public static logProviderNotFound(
    providerPartnerId: string,
    providerCount: number
  ): void {
    VideoProviderConfigurationLogger.log(
      VideoProviderConfigurationEvent.ProviderNotFound,
      MESSAGE_PROVIDER_NOT_FOUND,
      DiagnosticSeverity.Warning,
      {
        [KEY_PROVIDER_PARTNER_ID]: providerPartnerId,
        [KEY_PROVIDER_COUNT]: String(providerCount),
        [KEY_ERROR_CODE]: VideoProviderConfigurationErrorCode.UNAVAILABLE
      }
    );
    OperationalLogger.logError({
      errorCode: VideoProviderConfigurationErrorCode.UNAVAILABLE,
      errorMessage: MESSAGE_PROVIDER_NOT_FOUND
    });
  }

  /**
   * The provider's DRM configuration lacks fields DRM setup needs (`V909`).
   *
   * @param missingFields - Names of the absent fields, never their values
   */
  public static logDrmConfigInvalid(
    providerPartnerId: string,
    drmServerType: string | undefined,
    missingFields: ReadonlyArray<string>
  ): void {
    VideoProviderConfigurationLogger.log(
      VideoProviderConfigurationEvent.DrmConfigInvalid,
      MESSAGE_DRM_CONFIG_INVALID,
      DiagnosticSeverity.Warning,
      {
        [KEY_PROVIDER_PARTNER_ID]: providerPartnerId,
        [KEY_DRM_SERVER_TYPE]: drmServerType,
        [KEY_MISSING_FIELDS]: missingFields.join(","),
        [KEY_ERROR_CODE]: VideoProviderConfigurationErrorCode.MISCONFIGURATION
      }
    );
    OperationalLogger.logError({
      errorCode: VideoProviderConfigurationErrorCode.MISCONFIGURATION,
      errorMessage: MESSAGE_DRM_CONFIG_INVALID
    });
  }

  /**
   * DRM setup exhausted its retries and playback failed with a video provider
   * configuration error (`V934` or `V909`), which the player surfaces.
   */
  public static logPlaybackFailed(
    errorCode: string,
    providerPartnerId: string,
    drmErrorCount: number
  ): void {
    VideoProviderConfigurationLogger.log(
      VideoProviderConfigurationEvent.PlaybackFailed,
      MESSAGE_PLAYBACK_FAILED,
      DiagnosticSeverity.Error,
      {
        [KEY_PROVIDER_PARTNER_ID]: providerPartnerId,
        [KEY_DRM_ERROR_COUNT]: String(drmErrorCount),
        [KEY_ERROR_CODE]: errorCode
      }
    );
    OperationalLogger.logUiError({
      errorCode,
      errorMessage: MESSAGE_PLAYBACK_FAILED
    });
  }

  /** `V934` or `V934-N603` — the code as a user or support would see it. */
  private static toDisplayCode(
    fields: VideoProviderConfigurationLogFields
  ): string {
    const errorCode: string = fields[KEY_ERROR_CODE] || "";
    const subErrorCode: string | undefined = fields[KEY_SUB_ERROR_CODE];
    return subErrorCode !== undefined
      ? errorCode + "-" + subErrorCode
      : errorCode;
  }

  private static log(
    event: VideoProviderConfigurationEventName,
    message: string,
    severity: DiagnosticSeverity,
    extra?: VideoProviderConfigurationLogFields
  ): void {
    const details: LogDetails = { [KEY_EVENT]: event };
    if (extra !== undefined) {
      Object.keys(extra).forEach((key: string): void => {
        const value: string | undefined = extra[key];
        if (value !== undefined) {
          details[key] = value;
        }
      });
    }
    DiagnosticLogger.log(
      VIDEO_PROVIDER_CONFIGURATION_DIAGNOSTIC_EVENT,
      message,
      severity,
      details
    );
  }
}
